export interface RazorpayCheckoutResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayFailureResponse {
  error?: {
    description?: string;
  };
}

interface RazorpayInstance {
  open(): void;
  on(event: 'payment.failed', handler: (response: RazorpayFailureResponse) => void): void;
}

interface RazorpayOptions {
  key: string;
  order_id: string;
  amount: string;
  currency: string;
  name: string;
  description: string;
  handler: (response: RazorpayCheckoutResponse) => void;
  modal: { ondismiss: () => void };
}

interface RazorpayConstructor {
  new (options: RazorpayOptions): RazorpayInstance;
}

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

const checkoutScriptUrl = 'https://checkout.razorpay.com/v1/checkout.js';
let checkoutScriptPromise: Promise<void> | undefined;

async function loadCheckoutScript(): Promise<void> {
  if (window.Razorpay) return;

  if (!checkoutScriptPromise) {
    checkoutScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = checkoutScriptUrl;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        checkoutScriptPromise = undefined;
        reject(new Error('Razorpay Checkout could not be loaded. Check your connection and try again.'));
      };
      document.head.appendChild(script);
    });
  }

  await checkoutScriptPromise;
  if (!window.Razorpay) {
    checkoutScriptPromise = undefined;
    throw new Error('Razorpay Checkout loaded without its payment interface.');
  }
}

export async function openRazorpayCheckout(
  payload: Record<string, string>,
  handlers: {
    onSuccess: (response: RazorpayCheckoutResponse) => void;
    onFailure: (message: string) => void;
    onDismiss: () => void;
  },
): Promise<void> {
  const key = payload.keyId;
  const orderId = payload.orderId;
  const amount = payload.amount;
  const currency = payload.currency;
  if (!key || !orderId || !amount || !currency) {
    throw new Error('The server did not return all required Razorpay checkout details.');
  }

  await loadCheckoutScript();
  const Razorpay = window.Razorpay;
  if (!Razorpay) throw new Error('Razorpay Checkout is unavailable.');

  let completed = false;
  const checkout = new Razorpay({
    key,
    order_id: orderId,
    amount,
    currency,
    name: payload.name || 'RoomKart',
    description: payload.description || 'RoomKart subscription',
    handler: (response) => {
      if (completed) return;
      completed = true;
      handlers.onSuccess(response);
    },
    modal: {
      ondismiss: () => {
        if (completed) return;
        completed = true;
        handlers.onDismiss();
      },
    },
  });

  checkout.on('payment.failed', (response) => {
    if (completed) return;
    completed = true;
    handlers.onFailure(response.error?.description || 'Razorpay could not complete this payment.');
  });
  checkout.open();
}
